import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { BreadcrumbStoreService } from './breadcrumb-store.service';
import {
  BreadcrumbItem,
  BREADCRUMB_HIDDEN_KEY,
  BREADCRUMB_KEY,
  BreadcrumbRouteData,
  BreadcrumbDropdownItem,
} from '../breadcrumb.interfaces';

@Injectable()
export class BreadcrumbService {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);
  private readonly breadcrumbStoreService = inject(BreadcrumbStoreService);

  initRouteListener(): void {
    this.buildBreadcrumbs();

    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.buildBreadcrumbs();
      });
  }

  buildBreadcrumbs(): void {
    const isHidden = this.checkIfHidden();
    this.breadcrumbStoreService.setIsHidden(isHidden);

    if (isHidden) {
      this.breadcrumbStoreService.setItems([]);
      return;
    }

    const items = this.createBreadcrumbs(this.route.root);

    const uniqueItems = items.filter(
      (item, index, self) => index === self.findIndex(t => t.routerLink === item.routerLink)
    );

    const lastItem = uniqueItems.at(-1);
    if (lastItem) {
      lastItem.isLast = true;
    }

    this.breadcrumbStoreService.setItems(uniqueItems);
  }

  checkIfHidden(): boolean {
    let route: ActivatedRoute | null = this.route.root;

    while (route) {
      const snapshot = route.snapshot;

      if (snapshot?.data?.[BREADCRUMB_HIDDEN_KEY]) {
        return true;
      }

      route = route.firstChild ?? null;
    }

    return false;
  }

  createBreadcrumbs(
    route: ActivatedRoute,
    routerLink = '',
    breadcrumbs: BreadcrumbItem[] = []
  ): BreadcrumbItem[] {
    for (const child of route.children) {
      const snapshot = child.snapshot;
      const routeURL = snapshot?.url?.map(segment => segment.path).join('/') ?? '';
      if (routeURL !== '') {
        routerLink += `/${routeURL}`;
      }

      const item = this.buildBreadcrumbItem(child, routerLink);
      if (item) {
        breadcrumbs.push(item);
      }

      this.createBreadcrumbs(child, routerLink, breadcrumbs);
    }

    return breadcrumbs;
  }

  private buildBreadcrumbItem(child: ActivatedRoute, routerLink: string): BreadcrumbItem | null {
    // `ActivatedRouteSnapshot.data` is always merged down the whole active route chain in
    // Angular (unlike `params`), so a route with no breadcrumb data or resolver of its own
    // would otherwise silently inherit - and duplicate - its nearest ancestor's breadcrumb
    // entry. `routeConfig` is never inherited, so gate on it to check whether THIS route
    // declares its own breadcrumb, via either static `data` or a `resolve` function, before
    // reading the (possibly inherited) resolved value off the snapshot.
    const declaresOwnBreadcrumb = !!(
      child.routeConfig?.data?.[BREADCRUMB_KEY] || child.routeConfig?.resolve?.[BREADCRUMB_KEY]
    );

    if (!declaresOwnBreadcrumb) {
      return null;
    }

    const breadcrumbData = child.snapshot?.data?.[BREADCRUMB_KEY] as
      (BreadcrumbRouteData & { dropdownOptions?: BreadcrumbDropdownItem[] }) | undefined;

    if (!breadcrumbData || breadcrumbData.hidden) {
      return null;
    }

    const item: BreadcrumbItem = {
      routerLink: routerLink || '/',
      ...(breadcrumbData.label && { label: breadcrumbData.label }),
      ...(breadcrumbData.icon && { icon: breadcrumbData.icon }),
      ...(breadcrumbData.clickable === false && { clickable: false }),
      ...(breadcrumbData.dropdownOptions?.length && { dropdown: breadcrumbData.dropdownOptions }),
      ...(breadcrumbData.footerDropdownActions?.length && {
        footerDropdownActions: breadcrumbData.footerDropdownActions,
      }),
      ...(breadcrumbData.truncate && { truncate: breadcrumbData.truncate }),
    };

    return item;
  }
}
